/* eslint-disable no-param-reassign */
import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { store } from 'store';
import { isAdmin } from 'utils/permission';

export const baseURI = '/recaudacion/tipos-de-descuento/';
const NAVOJOA = process.env.REACT_APP_ENV_NAME === 'navojoa';

const parse2Digits = (num = 0) => parseFloat(parseFloat(num).toFixed(2));

/**
 * GET Tipos de Descuentos
 * @returns {Promise<Array<object>>}
 */
export const getTiposDeDescuentos = async (params = null) => {
  try {
    const response = await API.get(baseURI, { params });
    const _userRoles = store.getState().auth.roles.map((e) => e.id);
    if (!NAVOJOA) {
      return formatReceived(response.data).filter((e) => ![4, 2].includes(e.tipo_de_aplicacion))
        .map((e) => ({
          ...e,
          fechas: [e.fecha_inicio, e.fecha_fin],
          puede_aplicar: !e.roles_que_pueden_aplicar_descuento.length
        || e.roles_que_pueden_aplicar_descuento.some((r) => _userRoles.includes(r)),
          puede_autorizar: !e.roles_que_pueden_aplicar_descuento.length
        || e.roles_que_pueden_aplicar_descuento.some((r) => _userRoles.includes(r)),
        }));
    } return formatReceived(response.data)
      .map((e) => ({
        ...e,
        fechas: [e.fecha_inicio, e.fecha_fin],
        puede_aplicar: isAdmin() || !e.roles_que_pueden_aplicar_descuento.length
        || e.roles_que_pueden_aplicar_descuento.some((r) => _userRoles.includes(r)),
        puede_autorizar: isAdmin() || !e.roles_que_pueden_aplicar_descuento.length
        || e.roles_que_pueden_aplicar_descuento.some((r) => _userRoles.includes(r)),
      }));
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Tipo de Descuento
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getTipoDeDescuento = async (id) => {
  try {
    const _userRoles = store.getState().auth.roles.map((e) => e.id);
    const response = await API.get(`${baseURI}${id}`);
    const formatted = formatReceived(response.data);
    return {
      ...formatted,
      fechas: [formatted.fecha_inicio, formatted.fecha_fin],
      puede_aplicar: !formatted.roles_que_pueden_aplicar_descuento.length
        || formatted.roles_que_pueden_aplicar_descuento.some((r) => _userRoles.includes(r)),
      puede_autorizar: !formatted.roles_que_pueden_aplicar_descuento.length
        || formatted.roles_que_pueden_aplicar_descuento.some((r) => _userRoles.includes(r)),
    };
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Tipo de Descuento
  * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postTipoDeDescuento = async (values) => {
  try {
    if (values?.variables === null || values?.variables === undefined) {
      values.variables = [];
    }
    if (!values.el_descuento_es_por_rango_de_fechas) {
      values.fecha_inicio = null;
      values.fecha_fin = null;
      delete values.fechas;
    } else {
      const [fecha_inicio, fecha_fin] = values.fechas;
      values.fecha_inicio = fecha_inicio;
      values.fecha_fin = fecha_fin;
      delete values.fechas;
    }
    if (values.tipo_de_aplicacion === 2) {
      values.cantidad_maxima = parse2Digits(values.cantidad_maxima);
      values.cantidad_minima = parse2Digits(values.cantidad_minima);
    }
    const formattedValues = getFormattedValues(values, { clean: false });
    const response = await API.post(baseURI, formattedValues);
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Tipo de Descuento
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchTipoDeDescuento = async (id, values) => {
  try {
    if (values?.variables === null || values?.variables === undefined) {
      values.variables = [];
    }
    if (!values.el_descuento_es_por_rango_de_fechas) {
      values.fecha_inicio = null;
      values.fecha_fin = null;
      delete values.fechas;
    } else {
      const [fecha_inicio, fecha_fin] = values.fechas;
      values.fecha_inicio = fecha_inicio;
      values.fecha_fin = fecha_fin;
      delete values.fechas;
    }
    if (values.tipo_de_aplicacion === 2) {
      values.cantidad_maxima = parse2Digits(values.cantidad_maxima);
      values.cantidad_minima = parse2Digits(values.cantidad_minima);
    }
    const formattedValues = getFormattedValues(values);
    const response = await API.patch(`${baseURI}${id}`, formattedValues);
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Tipo de Descuento
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const deleteTipoDeDescuento = async (id) => {
  try {
    const response = await API.delete(`${baseURI}${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};
